import CloseIcon from '@mui/icons-material/Close'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PersonOffIcon from '@mui/icons-material/PersonOff'
import { Dialog, Box, Typography, IconButton, Button, CircularProgress, LinearProgress, Drawer, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AvatarComponentChat } from 'src/components/ChatUIComponents/chatComponents'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import {
  getImageLink,
  isCommonResponseSuccessful,
} from 'src/mentor/helpers/utilityFunctions'
import { CheckInDialogButtonComponent } from './checkInComponents'
import { checkIntoRoom } from './services'
import { styled } from '@mui/material/styles'
import { DISPATCH_EVENT_KEYS, SIGNALING_COMMANDS } from 'src/consumer/constants'
import { sendShowImageCommandToChamberScreen } from 'src/consumer/services/featureServices'

// Styled Components - Using theme spacing and standard design tokens
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3), // 24px
    overflow: 'hidden',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
    fontFamily: theme.typography.fontFamily,
    background: 'rgba(255, 255, 255, 0.98)',
    // Removed backdropFilter to prevent blur issues
    position: 'relative',
    zIndex: 1300,
    [theme.breakpoints.down('sm')]: {
      maxWidth: `calc(100% - ${theme.spacing(2)})`,
      maxHeight: '95vh',
      borderRadius: `${theme.spacing(3)} ${theme.spacing(3)} 0 0`,
    },
  },
  '& .MuiDialog-container': {
    overflowY: 'auto',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiBackdrop-root': {
    zIndex: 1299,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}))

const DialogHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 3, 3, 3), // 24px all sides
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4, 5, 4, 5), // 32px, 40px
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4.5, 6, 4.5, 6), // 36px, 48px
  },
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  flexShrink: 0,
  gap: theme.spacing(1.5), // 12px
  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(2), // 16px
  },
  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2), 0 2px 6px rgba(118, 75, 162, 0.15)',
}))

const DialogContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 3), // 24px
  paddingBottom: theme.spacing(5), // 40px - extra for progress bar
  background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
  position: 'relative',
  overflowY: 'auto',
  overflowX: 'hidden',
  flex: '1 1 auto',
  minHeight: 0,
  maxHeight: '100%',
  WebkitOverflowScrolling: 'touch',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 5), // 40px
    paddingBottom: theme.spacing(6.5), // 52px
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(6, 7), // 48px, 56px
    paddingBottom: theme.spacing(7.5), // 60px
  },
  [theme.breakpoints.down('md')]: {
    maxHeight: 'calc(95vh - 102px)', // Header + drag handle + padding
  },
  // Modern scrollbar styling
  '&::-webkit-scrollbar': {
    width: theme.spacing(1), // 8px
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: theme.spacing(0.5), // 4px
    margin: theme.spacing(1, 0), // 8px vertical
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(103, 121, 227, 0.3)',
    borderRadius: theme.spacing(0.5), // 4px
    border: `${theme.spacing(0.25)} solid transparent`, // 2px
    backgroundClip: 'padding-box',
    '&:hover': {
      background: 'rgba(103, 121, 227, 0.5)',
      backgroundClip: 'padding-box',
    },
  },
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(103, 121, 227, 0.3) rgba(0, 0, 0, 0.05)',
}))

const TimeoutProgressBar = styled(Box)(({ theme, progress }) => ({
  position: 'sticky',
  bottom: 0,
  left: 0,
  right: 0,
  height: theme.spacing(0.5), // 4px
  background: `linear-gradient(to right, #6779e3 0%, #6779e3 ${progress}%, rgba(103, 121, 227, 0.15) ${progress}%, rgba(103, 121, 227, 0.15) 100%)`,
  transition: 'background 0.1s linear',
  zIndex: 10,
  borderRadius: `0 0 ${theme.spacing(3)} ${theme.spacing(3)}`, // 0 0 24px 24px
  marginTop: 'auto',
  width: '100%',
  flexShrink: 0,
}))

const WelcomeSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5), // 12px
  padding: theme.spacing(2.5, 2), // 20px, 16px
  background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.08) 0%, rgba(118, 75, 162, 0.06) 100%)',
  borderRadius: theme.spacing(2.5), // 20px
  border: `2px solid rgba(103, 121, 227, 0.2)`,
  marginBottom: theme.spacing(2.5), // 20px
  boxShadow: '0 4px 16px rgba(103, 121, 227, 0.15), 0 2px 8px rgba(118, 75, 162, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
  // Removed backdropFilter to prevent blur issues on mobile
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(2), // 16px
    padding: theme.spacing(3, 2.5), // 24px, 20px
    marginBottom: theme.spacing(3), // 24px
  },
  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(2.25), // 18px
    padding: theme.spacing(3.5, 3), // 28px, 24px
    marginBottom: theme.spacing(3.5), // 28px
  },
  '&:hover': {
    boxShadow: '0 6px 24px rgba(103, 121, 227, 0.2), 0 3px 12px rgba(118, 75, 162, 0.15)',
    transform: 'translateY(-1px)',
    borderColor: 'rgba(103, 121, 227, 0.3)',
  },
}))

const InfoCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2.25), // 16px, 18px
  background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.15) 0%, rgba(118, 75, 162, 0.12) 100%)',
  borderRadius: theme.spacing(2), // 16px
  border: `2px solid rgba(103, 121, 227, 0.3)`,
  marginBottom: theme.spacing(2.25), // 18px
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.75), // 14px
  boxShadow: '0 4px 16px rgba(103, 121, 227, 0.2), 0 2px 8px rgba(118, 75, 162, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
  // Removed backdropFilter to prevent blur issues on mobile
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2.5, 3), // 20px, 24px
    marginBottom: theme.spacing(3), // 24px
    gap: theme.spacing(2.25), // 18px
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 3.5), // 24px, 28px
    marginBottom: theme.spacing(3.5), // 28px
    gap: theme.spacing(2.5), // 20px
  },
  '&:hover': {
    boxShadow: '0 6px 20px rgba(103, 121, 227, 0.25), 0 3px 10px rgba(118, 75, 162, 0.2)',
    transform: 'translateY(-2px)',
    borderColor: 'rgba(103, 121, 227, 0.4)',
  },
}))

const SuccessMessage = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 2.25), // 20px, 18px
  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.1) 100%)',
  borderRadius: theme.spacing(2.25), // 18px
  border: `2px solid rgba(16, 185, 129, 0.3)`,
  textAlign: 'center',
  marginTop: theme.spacing(2.25), // 18px
  boxShadow: '0 6px 20px rgba(16, 185, 129, 0.2), 0 3px 10px rgba(5, 150, 105, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
  // Removed backdropFilter to prevent blur issues on mobile
  transition: 'all 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3, 2.75), // 24px, 22px
    marginTop: theme.spacing(3), // 24px
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3.5, 3.25), // 28px, 26px
    marginTop: theme.spacing(3.5), // 28px
  },
  '&:hover': {
    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.25), 0 4px 12px rgba(5, 150, 105, 0.2)',
    transform: 'translateY(-2px)',
  },
}))

const ErrorCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 2.25), // 20px, 18px
  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.1) 100%)',
  borderRadius: theme.spacing(2.25), // 18px
  border: `2px solid rgba(239, 68, 68, 0.3)`,
  boxShadow: '0 6px 20px rgba(239, 68, 68, 0.2), 0 3px 10px rgba(220, 38, 38, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
  // Removed backdropFilter to prevent blur issues on mobile
  transition: 'all 0.3s ease',
  textAlign: 'center',
  marginTop: theme.spacing(2.25), // 18px
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3, 2.75), // 24px, 22px
    marginTop: theme.spacing(3), // 24px
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3.5, 3.25), // 28px, 26px
    marginTop: theme.spacing(3.5), // 28px
  },
  '&:hover': {
    boxShadow: '0 8px 24px rgba(239, 68, 68, 0.25), 0 4px 12px rgba(220, 38, 38, 0.2)',
    transform: 'translateY(-2px)',
  },
}))

const OccupiedCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 2.25), // 20px, 18px
  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.1) 100%)',
  borderRadius: theme.spacing(2.25), // 18px
  border: `2px solid rgba(245, 158, 11, 0.3)`,
  boxShadow: '0 6px 20px rgba(245, 158, 11, 0.2), 0 3px 10px rgba(217, 119, 6, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
  // Removed backdropFilter to prevent blur issues on mobile
  transition: 'all 0.3s ease',
  textAlign: 'center',
  marginTop: theme.spacing(2.25), // 18px
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3, 2.75), // 24px, 22px
    marginTop: theme.spacing(3), // 24px
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3.5, 3.25), // 28px, 26px
    marginTop: theme.spacing(3.5), // 28px
  },
  '&:hover': {
    boxShadow: '0 8px 24px rgba(245, 158, 11, 0.25), 0 4px 12px rgba(217, 119, 6, 0.2)',
    transform: 'translateY(-2px)',
  },
}))

export default function CheckInDialogComponent({
  closeFunction,
  onChoosingInPerson,
  onChoosingRemote,
  roomData,
  clinicLocationData,
  showJoinHotline,
  hotlineOnClick,
  disableTimeout = false, // Option to disable auto-close timeout
}) {
  const IN_PERSON_CHECKIN_METHOD = 'check-in-method-in-person'
  const REMOTE_CHECKIN_METHOD = 'check-in-method-remote'
  const TIMEOUT_DURATION = 30000 // 30 seconds


  const [selectedCheckinMethod, setSelectedCheckinMethod] = useState()
  const [selectedCheckinEventOption, setSelectedCheckinEventOption] = useState()
  const { mentorProfileData } = useContext(MentorProfileContext)
  const [page, setPage] = useState('checkinMethodChoicePage') // checkinMethodChoicePage, checkInEventChoicePage, checkInFinalPage

  const [continueLoading, setContinueLoading] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(TIMEOUT_DURATION)
  const [checkInError, setCheckInError] = useState(null)
  const [roomOccupied, setRoomOccupied] = useState(false)
  const [checkedInSupportTeam, setCheckedInSupportTeam] = useState(null)

  // Refs for scrolling to new content
  const contentBoxRef = useRef(null)
  const eventOptionsRef = useRef(null)
  const finalPageRef = useRef(null)
  
  // Store original scroll position and styles
  const scrollRestoreRef = useRef(null)

  // Use ref to track if check-in has been initiated to prevent multiple calls
  const checkInInitiatedRef = useRef(false)

  // Navigation for background calendar loading
  const navigate = useNavigate()

  async function performCheckIn() {
    setContinueLoading(true)
    setCheckInError(null)
    setCheckedInSupportTeam(null)
    
    try {
      const response = await checkIntoRoom({
        ...roomData,
        selectedCheckinEventOption: selectedCheckinEventOption,
        checkinMethod: selectedCheckinMethod,
      })
      
      setContinueLoading(false)
      
      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        console.log(response?.data?.data)
        setCheckedInSupportTeam({
          checkedInClinicLocation: response?.data?.data?.checkedInClinicLocation,
          checkedInTherapists: response?.data?.data?.checkedInTherapists || [],
          checkedInScreeners: response?.data?.data?.checkedInScreeners || [],
        })
        window.dispatchEvent(
          new CustomEvent(DISPATCH_EVENT_KEYS.userCheckedIn, {
            detail: {
              roomData: response?.data?.data?.['chamber-room'],
              usageLog: response?.data?.data?.['emr-usage-log'],
            },
          }),
        )
        localStorage.setItem(
          'checkedInDetails',
          JSON.stringify({
            ...response?.data?.data?.['emr-usage-log'],
            roomName: response?.data?.data?.['chamber-room']?.title,
          }),
        )
        
        // Preload next page based on check-in method in the background
        // Keep popup open while loading - user can close manually
        if (clinicLocationData?.clinicBrandId && clinicLocationData?.id) {
          let nextPagePath = null
          
          if (selectedCheckinMethod === IN_PERSON_CHECKIN_METHOD) {
            // For in-person, preload the calendar/bookings page
            nextPagePath = `/mentor-home/my-clinic-associations/${clinicLocationData.clinicBrandId}/${clinicLocationData.id}`
          } else if (selectedCheckinMethod === REMOTE_CHECKIN_METHOD) {
            // For remote, preload the chamber room page
            nextPagePath = `/mentor-home/join-chamber-room/${clinicLocationData.clinicBrandId}/${clinicLocationData.id}`
          }
          
          if (nextPagePath) {
            // Proceed to the post-check-in page automatically once the check-in is persisted, so a
            // successful check-in lands the user on the calendar/chamber page directly. Previously
            // this only prefetched in the background and the user had to close the success card to
            // actually navigate — which read as "nothing happened" until a second action.
            setTimeout(() => {
              navigate(nextPagePath, { replace: false })
            }, 500)
          }
        }
        
        // Don't auto-close - let user close manually when ready
        // The popup will stay open while background page loads
      } else {
        // Code 4296 = the room is actively in use by another practitioner (the server declined the
        // silent takeover). Surface the occupied state so the user can request checkout instead. A
        // stale occupant, by contrast, would have been taken over and handled by the success path.
        const errorMessage = response?.data?.message || 'Check-in failed'
        if (
          response?.data?.code === 4296 ||
          errorMessage.toLowerCase().includes('occupied') ||
          errorMessage.toLowerCase().includes('already checked in')
        ) {
          setRoomOccupied(true)
        } else {
          setCheckInError(errorMessage)
        }
      }
    } catch (error) {
      setContinueLoading(false)
      setCheckInError(error?.message || 'An unexpected error occurred during check-in')
    }
  }

  // Timeout countdown - use ref for handleClose to prevent effect re-runs
  const handleCloseRef = useRef(null)

  useEffect(() => {
    // Skip timeout if disabled or during check-in process
    if (disableTimeout || (page === 'checkInFinalPage' && continueLoading)) {
      return
    }

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 100) {
          // Silently close after timeout - use ref to get latest handleClose
          if (handleCloseRef.current) {
            handleCloseRef.current()
          }
          return 0
        }
        return prev - 100
      })
    }, 100)

    return () => {
      clearInterval(interval)
    }
  }, [page, continueLoading, disableTimeout]) // Added disableTimeout to dependencies

  // Intentionally do NOT pre-block when the room is shown as occupied. The check-in is attempted and
  // the server decides: it takes the room over when the occupant looks stale (a forgotten checkout or
  // a rostered hand-over) and only rejects (code 4296) when they are actively present — at which point
  // performCheckIn surfaces the "occupied" state below so the user can request checkout instead.

  useEffect(() => {
    // if (
    //   Array.isArray(roomData?.checkInMethodOptions) &&
    //   roomData?.checkInMethodOptions?.length === 1
    // ) {
    //   setSelectedCheckinMethod(roomData?.checkInMethodOptions?.[0]?.id)
    // }
    if (
      Array.isArray(roomData?.checkInEventOptions) &&
      roomData?.checkInEventOptions?.length === 1
    ) {
      setSelectedCheckinEventOption(roomData?.checkInEventOptions?.[0]?.id)
    }
    setPage('checkinMethodChoicePage') // Start with method choice page
    setTimeRemaining(TIMEOUT_DURATION) // Reset timer when dialog opens
  }, [])

  useEffect(() => {
    // Auto-advance to final page when both selections are made
    if (page === 'checkinMethodChoicePage' && Boolean(selectedCheckinMethod) && Boolean(selectedCheckinEventOption)) {
      setPage('checkInFinalPage')
    }
  }, [selectedCheckinMethod, selectedCheckinEventOption, page])

  // Separate effect for performing check-in to avoid state updates during render
  useEffect(() => {
    if (page === 'checkInFinalPage' && !checkInInitiatedRef.current) {
      checkInInitiatedRef.current = true
      // Use setTimeout to defer the check-in call to avoid state updates during render
      setTimeout(() => {
        performCheckIn()
      }, 0)
    }
    // Reset flag when page changes away from final page
    if (page !== 'checkInFinalPage') {
      checkInInitiatedRef.current = false
    }
  }, [page])

  // Scroll to event options when they appear
  useEffect(() => {
    if (selectedCheckinMethod && eventOptionsRef.current && contentBoxRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const container = contentBoxRef.current
        const target = eventOptionsRef.current
        if (container && target) {
          const containerRect = container.getBoundingClientRect()
          const targetRect = target.getBoundingClientRect()
          const scrollTop = container.scrollTop + (targetRect.top - containerRect.top) - 20 // 20px offset from top
          container.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })
        }
      }, 150)
    }
  }, [selectedCheckinMethod])

  // Scroll to final page when it appears
  useEffect(() => {
    if (page === 'checkInFinalPage' && finalPageRef.current && contentBoxRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const container = contentBoxRef.current
        const target = finalPageRef.current
        if (container && target) {
          const containerRect = container.getBoundingClientRect()
          const targetRect = target.getBoundingClientRect()
          const scrollTop = container.scrollTop + (targetRect.top - containerRect.top) - 20 // 20px offset from top
          container.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })
        }
      }, 150)
    }
  }, [page])

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const checkedInTherapists = checkedInSupportTeam?.checkedInTherapists || []
  const checkedInScreeners = checkedInSupportTeam?.checkedInScreeners || []
  const supportTeamMemberCount = checkedInTherapists.length + checkedInScreeners.length
  const supportTeamLocationTitle =
    checkedInSupportTeam?.checkedInClinicLocation?.title || clinicLocationData?.title || 'this clinic'
  const getSupportTeamImage = (member) =>
    member?.profilePicId || member?.profilePicUrl
      ? getImageLink(member?.profilePicId, member?.profilePicUrl, false)
      : null
  const getSupportTeamEmptyState = (title) =>
    `No ${title.toLowerCase()} checked in yet at ${supportTeamLocationTitle}.`

  // Force remove blur from backdrop and drawer after render (handles timing issues)
  useEffect(() => {

    const removeBlur = () => {
      // Find all backdrop elements and remove blur
      const backdrops = document.querySelectorAll('.MuiBackdrop-root')

      backdrops.forEach((backdrop, index) => {
        if (backdrop) {
          backdrop.style.setProperty('backdrop-filter', 'none', 'important')
          backdrop.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
          backdrop.style.setProperty('background-color', 'rgba(0, 0, 0, 0.4)', 'important')
        }
      })

      // Also remove blur from drawer/dialog paper
      const drawerPapers = document.querySelectorAll('.MuiDrawer-paper, .MuiDialog-paper')
      drawerPapers.forEach((paper) => {
        if (paper) {
          paper.style.setProperty('backdrop-filter', 'none', 'important')
          paper.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
          // CRITICAL: Ensure drawer content is above backdrop - must be higher than backdrop (1399)
          const currentZIndex = parseInt(window.getComputedStyle(paper).zIndex) || 0
          if (currentZIndex < 1400) {
            paper.style.setProperty('z-index', '1401', 'important')
          }
          paper.style.setProperty('pointer-events', 'auto', 'important')
        }
      })

      // Check body and html elements for blur
      const bodyStyle = window.getComputedStyle(document.body)
      const htmlStyle = window.getComputedStyle(document.documentElement)
      const bodyBlur = bodyStyle.backdropFilter || bodyStyle.webkitBackdropFilter || 'none'
      const htmlBlur = htmlStyle.backdropFilter || htmlStyle.webkitBackdropFilter || 'none'
      if (bodyBlur !== 'none') {
        document.body.style.setProperty('backdrop-filter', 'none', 'important')
        document.body.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
      }
      if (htmlBlur !== 'none') {
        document.documentElement.style.setProperty('backdrop-filter', 'none', 'important')
        document.documentElement.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
      }

      // Specifically target header elements with hide-on-print-only class (known blur source)
      // Use both inline styles AND inject a CSS rule to override styled-components
      const headerElements = document.querySelectorAll('.hide-on-print-only, [class*="sc-fTZNKr"]')
      headerElements.forEach((el) => {
        const style = window.getComputedStyle(el)
        const blurValue = style.backdropFilter || style.webkitBackdropFilter || 'none'
        if (blurValue !== 'none') {
          // Use inline styles with !important - multiple attempts to ensure it sticks
          el.style.setProperty('backdrop-filter', 'none', 'important')
          el.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
          el.style.setProperty('filter', 'none', 'important')
          el.style.setProperty('-webkit-filter', 'none', 'important')
          // Also add a class that we can target with CSS
          el.classList.add('no-blur-override')
          // Force a reflow to ensure styles are applied
          void el.offsetHeight
        }
      })
      
      // Inject a global CSS rule to override styled-components for all hide-on-print-only elements
      // Use very high specificity to override styled-components
      let styleElement = document.getElementById('checkin-blur-removal-style')
      if (!styleElement) {
        styleElement = document.createElement('style')
        styleElement.id = 'checkin-blur-removal-style'
        document.head.appendChild(styleElement)
      }
      styleElement.textContent = `
        /* Target all possible header elements with blur */
        .hide-on-print-only,
        .hide-on-print-only *,
        div.hide-on-print-only,
        div[class*="sc-"].hide-on-print-only,
        div[class*="sc-"][class*="hide-on-print-only"],
        [class*="sc-fTZNKr"],
        [class*="sc-fTZNKr"] * {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          filter: none !important;
          -webkit-filter: none !important;
          transition: none !important;
          animation: none !important;
        }
        /* Also target sticky positioned elements that might be headers */
        [style*="position: sticky"][class*="hide-on-print-only"],
        [style*="position:sticky"][class*="hide-on-print-only"] {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          filter: none !important;
          -webkit-filter: none !important;
          transition: none !important;
          animation: none !important;
        }
        /* Target MuiBox elements inside the dialog that might have blur */
        .MuiDrawer-paper .MuiBox-root,
        .MuiDialog-paper .MuiBox-root,
        [class*="MuiBox-root"][class*="css-"] {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          filter: none !important;
          -webkit-filter: none !important;
        }
        /* Disable any backdrop transitions/animations and pseudo-elements */
        .MuiBackdrop-root {
          transition: none !important;
          animation: none !important;
        }
        .MuiBackdrop-root::before,
        .MuiBackdrop-root::after {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
          filter: none !important;
          -webkit-filter: none !important;
        }
      `

      // Remove blur from ANY element with blur, regardless of position or z-index
      // Also check for CSS filter property (not just backdrop-filter)
      const allElements = document.querySelectorAll('*')
      allElements.forEach((el) => {
        const style = window.getComputedStyle(el)
        const blurValue = style.backdropFilter || style.webkitBackdropFilter || 'none'
        const filterValue = style.filter || style.webkitFilter || 'none'
        const hasBackdropBlur = blurValue !== 'none'
        const hasFilterBlur = filterValue !== 'none' && filterValue.includes('blur')
        
        if (hasBackdropBlur) {
          el.style.setProperty('backdrop-filter', 'none', 'important')
          el.style.setProperty('-webkit-backdrop-filter', 'none', 'important')
        }
        
        if (hasFilterBlur) {
          // Remove blur from filter, but keep other filter effects if any
          const newFilter = filterValue.replace(/blur\([^)]+\)/g, '').trim()
          el.style.setProperty('filter', newFilter || 'none', 'important')
          el.style.setProperty('-webkit-filter', newFilter || 'none', 'important')
        }
      })
    }

    // Remove blur immediately and on next frame (handles Material-UI timing)
    removeBlur()
    requestAnimationFrame(() => {
      requestAnimationFrame(removeBlur)
    })

    // Also check periodically in case Material-UI or styled-components re-applies styles
    const interval = setInterval(() => {
      removeBlur()
    }, 50)

    return () => {
      clearInterval(interval)
      // Clean up the injected style element when component unmounts
      const styleElement = document.getElementById('checkin-blur-removal-style')
      if (styleElement) {
        styleElement.remove()
      }
    }
  }, [isMobile])

  // Shared content component
  const renderContent = () => (
    <>
      {page !== 'checkInFinalPage' && (
        <DialogHeader>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700, 
                fontSize: { xs: '22px', sm: '26px', md: '28px' }, 
                letterSpacing: '-0.02em',
                fontFamily: theme.typography.fontFamily,
                color: '#ffffff',
                lineHeight: 1.2,
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
            >
              Check In
            </Typography>
            {(
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {roomData?.title && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.9)',
                      letterSpacing: '0.01em',
                      minWidth: { xs: 7.5, sm: 8.75, md: 10 },
                    }}
                  >
                    Room:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: 500,
                      color: '#ffffff',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {roomData.title}
                  </Typography>
                </Box>
              )}
              {clinicLocationData?.title && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.9)',
                      letterSpacing: '0.01em',
                      minWidth: { xs: 7.5, sm: 8.75, md: 10 },
                    }}
                  >
                    Clinic:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: 500,
                      color: '#ffffff',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {clinicLocationData.title}
                  </Typography>
                </Box>
              )}
              {roomData?.clinicBrandName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.9)',
                      letterSpacing: '0.01em',
                      minWidth: { xs: 7.5, sm: 8.75, md: 10 },
                    }}
                  >
                    Brand:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: theme.typography.body2.fontSize,
                      fontWeight: 500,
                      color: '#ffffff',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {roomData.clinicBrandName}
                  </Typography>
                </Box>
              )}
            </Box>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: '#ffffff',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              width: { xs: 4.5, sm: 5 },
              height: { xs: 4.5, sm: 5 },
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <CloseIcon sx={{ fontSize: { xs: 2.5, sm: 2.75 } }} />
          </IconButton>
        </DialogHeader>
      )}

      <DialogContentBox 
        ref={contentBoxRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Room Occupied Warning */}
        {roomOccupied && (
          <OccupiedCard>
            <PersonOffIcon sx={{ 
              fontSize: { xs: 4, sm: 4.5, md: 5 }, 
              color: '#f59e0b', 
              mb: { xs: 1.5, sm: 2, md: 2.5 } 
            }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#92400e',
                mb: { xs: 1, sm: 1.5, md: 2 },
                fontSize: { xs: theme.typography.body1.fontSize, sm: '17px', md: theme.typography.h6.fontSize },
                fontFamily: theme.typography.fontFamily,
              }}
            >
              Room In Active Use
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#78350f', 
                fontSize: theme.typography.body2.fontSize, 
                mb: { xs: 2.5, sm: 3, md: 3.5 },
                lineHeight: 1.75,
                maxWidth: { xs: '100%', sm: 62.5, md: 75 },
                mx: 'auto',
              }}
            >
              This room is actively in use by{' '}
              <strong>{roomData?.checkedInMentorName || 'another mentor'}</strong>.
              <br />
              <br />
              To take it over, send a checkout request from the clinic view, or select a different room.
            </Typography>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                mt: { xs: 2, sm: 2.5, md: 3 },
                color: '#f59e0b',
                borderColor: '#f59e0b',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 3, sm: 3.5, md: 4 },
                py: { xs: 1.25, sm: 1.5, md: 1.75 },
                fontSize: theme.typography.body2.fontSize,
                borderRadius: 1.5, // 12px
                '&:hover': {
                  backgroundColor: 'rgba(245, 158, 11, 0.1)',
                  borderColor: '#f59e0b',
                },
              }}
            >
              Close
            </Button>
          </OccupiedCard>
        )}

        {/* Check-in Error */}
        {checkInError && !roomOccupied && (
          <ErrorCard>
            <Box
              sx={{
                width: { xs: 8, sm: 9, md: 10 },
                height: { xs: 8, sm: 9, md: 10 },
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.12) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: { xs: 2.5, sm: 3, md: 3.5 },
                border: `2px solid rgba(239, 68, 68, 0.3)`,
              }}
            >
              <ErrorOutlineIcon sx={{ 
                fontSize: { xs: 4, sm: 4.5, md: 5 }, 
                color: '#ef4444' 
              }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#991b1b',
                mb: { xs: 1.5, sm: 2, md: 2.5 },
                fontSize: { xs: theme.typography.h6.fontSize, sm: '20px', md: theme.typography.h5.fontSize },
                fontFamily: theme.typography.fontFamily,
                letterSpacing: '-0.01em',
              }}
            >
              Check-in Failed
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#7f1d1d', 
                fontSize: theme.typography.body1.fontSize, 
                mb: { xs: 3, sm: 3.5, md: 4 },
                lineHeight: 1.75,
                maxWidth: { xs: '100%', sm: 50, md: 62.5 },
                mx: 'auto',
              }}
            >
              {checkInError}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                mt: { xs: 1.5, sm: 2, md: 2.5 },
                backgroundColor: '#ef4444',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                px: { xs: 3.5, sm: 4, md: 4.5 },
                py: { xs: 1.25, sm: 1.5, md: 1.75 },
                borderRadius: 1.5, // 12px
                fontSize: theme.typography.body2.fontSize,
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                '&:hover': {
                  backgroundColor: '#dc2626',
                  boxShadow: '0 6px 16px rgba(239, 68, 68, 0.4)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Close
            </Button>
          </ErrorCard>
        )}

        {/* selecting checkin method */}
        {page === 'checkinMethodChoicePage' && !roomOccupied && !checkInError && (
          <Box sx={{ pb: { xs: 3, sm: 4, md: 5 } }}>
            <Box sx={{ 
              textAlign: 'center', 
              mb: { xs: 3, sm: 4, md: 5 },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1, sm: 1.25, md: 1.5 },
              alignItems: 'center',
            }}>
              <Typography
                variant="h4"
                sx={{
                  color: theme.palette.text.primary || '#1e293b',
                  fontWeight: 700,
                  fontSize: { xs: '22px', sm: '28px', md: '32px' },
                  letterSpacing: '-0.03em',
                  fontFamily: theme.typography.fontFamily,
                  lineHeight: 1.2,
                  mb: { xs: 0.5, sm: 0.75, md: 1 },
                  maxWidth: { xs: '100%', sm: '600px', md: '700px' },
                  width: '100%',
                }}
              >
                Choose your check-in method
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary || '#64748b',
                  fontSize: theme.typography.body1.fontSize,
                  fontWeight: 400,
                  lineHeight: 1.7,
                  maxWidth: { xs: '100%', sm: '500px', md: '600px' },
                  width: { xs: '100%', sm: 'auto' },
                  mx: 'auto',
                  px: { xs: 2, sm: 0, md: 0 },
                }}
              >
                Select how you&apos;ll be available for consultations today
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 2, sm: 2.5, md: 3 },
                mb: { xs: 2, sm: 2.5, md: 3 },
                width: '100%',
                maxWidth: { xs: '100%', sm: '500px', md: '600px' },
                mx: 'auto',
                px: { xs: 0, sm: 0 },
              }}
            >
              {(roomData?.checkInMethodOptions || [])?.map((item, index) => (
                <CheckInDialogButtonComponent
                  key={index}
                  id={item?.id}
                  imageLink={item?.imageLink}
                  disabled={!item?.enabled}
                  selected={selectedCheckinMethod === item?.id}
                  text={item?.title}
                  description={item?.description}
                  fullWidth={true}
                  clickFunc={() => {
                    if (item?.enabled) {
                      setSelectedCheckinMethod(item?.id)
                    }
                  }}
                />
              ))}
            </Box>
            
            {/* Show event options inline when method is selected */}
            {selectedCheckinMethod && (
              <Box 
                ref={eventOptionsRef}
                sx={{ 
                  mt: { xs: 3, sm: 4, md: 5 },
                  pt: { xs: 4, sm: 5, md: 6 },
                  borderTop: `1px solid ${theme.palette.divider || '#e2e8f0'}`,
                }}>
                <Box sx={{ 
                  textAlign: 'center', 
                  mb: { xs: 3, sm: 4, md: 5 },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: { xs: 1.25, sm: 1.5, md: 1.75 },
                  alignItems: 'center',
                }}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: theme.palette.text.primary || '#1e293b',
                      fontWeight: 700,
                      fontSize: { xs: '22px', sm: '28px', md: '30px' },
                      letterSpacing: '-0.02em',
                      fontFamily: theme.typography.fontFamily,
                      lineHeight: 1.2,
                      maxWidth: { xs: '100%', sm: '600px', md: '700px' },
                      width: '100%',
                    }}
                  >
                    Select session type
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary || '#64748b',
                      fontSize: theme.typography.body1.fontSize,
                      fontWeight: 400,
                      lineHeight: 1.75,
                      maxWidth: { xs: '100%', sm: '600px', md: '700px' },
                      width: '100%',
                      mx: 'auto',
                      px: { xs: 2, sm: 0, md: 0 },
                    }}
                  >
                    Choose the type of consultation you&apos;ll be conducting
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 2, sm: 2.5, md: 3 },
                    maxWidth: { xs: '100%', sm: '500px', md: '600px' },
                    width: '100%',
                    mx: 'auto',
                    px: { xs: 0, sm: 0, md: 0 },
                  }}
                >
                  {(roomData?.checkInEventOptions || [])?.map((item, index) => (
                    <CheckInDialogButtonComponent
                      key={index}
                      id={item?.id}
                      imageLink={item?.imageLink}
                      selected={selectedCheckinEventOption === item?.id}
                      text={item?.title}
                      description={item?.description}
                      fullWidth={true}
                      clickFunc={() => {
                        setSelectedCheckinEventOption(item?.id)
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        {/* selecting event choice - This page is now shown inline above, but keeping for backward compatibility */}
        {page === 'checkInEventChoicePage' && !roomOccupied && !checkInError && roomData?.showLegacyEventChoicePage && (
          <Box sx={{ 
            pb: { xs: 2, sm: 4 },
            minHeight: 0, // Allow flexbox to shrink
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Compact Welcome Section for Mobile */}
            <WelcomeSection
              sx={{
                mb: { xs: 2, sm: 2.5 },
                flexShrink: 0,
              }}
            >
              <AvatarComponentChat
                imageInHeader={
                  mentorProfileData?.profilePicId
                    ? getImageLink(mentorProfileData?.profilePicId)
                    : null
                }
                nameInHeader={mentorProfileData?.name || mentorProfileData?.title}
                showPresence={false}
              />
              <Box sx={{ textAlign: 'center', flex: 1 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#0f172a', 
                    mb: { xs: 0.5, sm: 0.75 },
                    fontSize: { xs: '15px', sm: '17px' },
                    letterSpacing: '-0.01em',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {mentorProfileData?.name || mentorProfileData?.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: { xs: 13, sm: 16 }, color: '#6779e3' }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#64748b', 
                      fontSize: { xs: '11px', sm: '13px' },
                      fontWeight: 400,
                    }}
                  >
                    {dayjs().format('h:mm A')}
                  </Typography>
                </Box>
              </Box>
            </WelcomeSection>
            
            <Typography
              variant="h5"
              sx={{
                textAlign: 'center',
                mb: { xs: 0.75, sm: 1 },
                color: '#1e293b',
                fontWeight: 700,
                fontSize: { xs: '20px', sm: '24px' },
                letterSpacing: '-0.02em',
                fontFamily: "'Inter', sans-serif",
                lineHeight: 1.3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                flexShrink: 0,
              }}
            >
              Select session type
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                mb: { xs: 2.5, sm: 3.5 },
                color: '#64748b',
                fontSize: { xs: '14px', sm: '15px' },
                fontWeight: 400,
                lineHeight: 1.7,
                maxWidth: '420px',
                mx: 'auto',
                px: { xs: 1, sm: 0 },
                flexShrink: 0,
              }}
            >
              Choose the type of consultation you&apos;ll be conducting
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: { xs: 2, sm: 2.5, md: 3 },
                alignItems: 'stretch',
                maxWidth: { xs: '100%', sm: '500px', md: '600px' },
                mx: 'auto',
                width: '100%',
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                pb: { xs: 1, sm: 0 },
                // Smooth scrolling for activity options
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {(roomData?.checkInEventOptions || [])?.map((item, index) => (
                <CheckInDialogButtonComponent
                  key={index}
                  id={item?.id}
                  imageLink={item?.imageLink}
                  selected={selectedCheckinEventOption === item?.id}
                  text={item?.title}
                  description={item?.description}
                  fullWidth={true}
                  clickFunc={() => {
                    setSelectedCheckinEventOption(item?.id)
                    setPage('checkInFinalPage')
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        {page === 'checkInFinalPage' && !roomOccupied && !checkInError && (
          <Box
            ref={finalPageRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: '220px', sm: '280px', md: '320px' },
              pb: { xs: 2.5, sm: 3, md: 4 },
              width: '100%',
            }}
          >
            {continueLoading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: { xs: 2.5, sm: 3, md: 3.5 }, 
                width: '100%', 
                py: { xs: 2.5, sm: 3, md: 4 },
                maxWidth: { xs: '100%', sm: '500px', md: '600px' },
                mx: 'auto',
                px: { xs: 2, sm: 0, md: 0 },
              }}>
                <Box
                  sx={{
                    width: { xs: 7, sm: 8, md: 9 },
                    height: { xs: 7, sm: 8, md: 9 },
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, rgba(103, 121, 227, 0.08) 0%, rgba(116, 80, 170, 0.08) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: { xs: 0.5, sm: 0.75, md: 1 },
                    flexShrink: 0,
                  }}
                >
                  <CircularProgress size={48} sx={{ color: '#6779e3' }} thickness={4} />
                </Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.primary || '#0f172a', 
                    fontWeight: 600,
                    fontSize: { xs: theme.typography.h6.fontSize, sm: '18px', md: '20px' },
                    letterSpacing: '-0.01em',
                    fontFamily: theme.typography.fontFamily,
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  Processing check-in
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary || '#64748b', 
                    fontSize: theme.typography.body2.fontSize,
                    fontWeight: 400,
                    textAlign: 'center',
                    maxWidth: { xs: '100%', sm: '400px', md: '500px' },
                    width: '100%',
                    lineHeight: 1.6,
                    px: { xs: 1, sm: 0, md: 0 },
                  }}
                >
                  Setting up your session. Please wait...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ 
                width: '100%', 
                pb: { xs: 2, sm: 2.5, md: 3 },
                pt: { xs: 3, sm: 4, md: 5 },
                maxWidth: { xs: '100%', sm: '600px', md: '700px' },
                mx: 'auto',
                px: { xs: 2, sm: 3, md: 4 },
                position: 'relative',
              }}>
                {/* Close Button for Success Page */}
                <IconButton
                  onClick={() => {
                    // Navigate to next page when user closes
                    if (selectedCheckinMethod === IN_PERSON_CHECKIN_METHOD) {
                      onChoosingInPerson()
                    } else if (selectedCheckinMethod === REMOTE_CHECKIN_METHOD) {
                      onChoosingRemote()
                    } else {
                      handleClose()
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: { xs: 1, sm: 1.5, md: 2 },
                    right: { xs: 1, sm: 1.5, md: 2 },
                    color: theme.palette.text.secondary || '#64748b',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    width: { xs: 3.5, sm: 4 },
                    height: { xs: 3.5, sm: 4 },
                    flexShrink: 0,
                    zIndex: 10,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      color: theme.palette.text.primary || '#1e293b',
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 2, sm: 2.25 } }} />
                </IconButton>

                {/* Compact Success Content */}
                <Box sx={{
                  textAlign: 'center',
                  mb: { xs: 2.5, sm: 3, md: 3.5 },
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2, md: 2.5 },
                }}>
                  {/* Success Icon with Greeting */}
                  <Box
                    sx={{
                      width: { xs: 8, sm: 10, md: 12 },
                      height: { xs: 8, sm: 10, md: 12 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '3px solid rgba(16, 185, 129, 0.4)',
                      boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3), 0 4px 12px rgba(5, 150, 105, 0.2)',
                      animation: 'pulse 2s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': {
                          transform: 'scale(1)',
                          boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3), 0 4px 12px rgba(5, 150, 105, 0.2)',
                        },
                        '50%': {
                          transform: 'scale(1.05)',
                          boxShadow: '0 12px 32px rgba(16, 185, 129, 0.4), 0 6px 16px rgba(5, 150, 105, 0.3)',
                        },
                      },
                    }}
                  >
                    <CheckCircleIcon sx={{ 
                      fontSize: { xs: 4, sm: 5, md: 6 }, 
                      color: '#10b981' 
                    }} />
                  </Box>

                  {/* Greeting */}
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary || '#0f172a',
                        fontSize: { xs: '22px', sm: '26px', md: '30px' },
                        letterSpacing: '-0.02em',
                        fontFamily: theme.typography.fontFamily,
                        mb: { xs: 0.5, sm: 0.75 },
                        lineHeight: 1.2,
                      }}
                    >
                      Welcome, {mentorProfileData?.name?.split(' ')[0] || mentorProfileData?.firstName || 'there'}!
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#059669',
                        fontSize: { xs: '16px', sm: '18px', md: '20px' },
                        letterSpacing: '-0.01em',
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Check-in Successful
                    </Typography>
                    {supportTeamMemberCount > 0 && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: { xs: 0.75, sm: 1 },
                          color: theme.palette.text.secondary || '#64748b',
                          fontSize: { xs: '13px', sm: '14px' },
                          lineHeight: 1.6,
                          maxWidth: { xs: '100%', sm: '440px' },
                          mx: 'auto',
                        }}
                      >
                        {supportTeamMemberCount} support teammate{supportTeamMemberCount > 1 ? 's are' : ' is'} already in service at {supportTeamLocationTitle}.
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Compact Session Info Card */}
                <InfoCard sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 1, sm: 1.25 } }}>
                    <AvatarComponentChat
                      imageInHeader={
                        mentorProfileData?.profilePicId
                          ? getImageLink(mentorProfileData?.profilePicId)
                          : null
                      }
                      nameInHeader={mentorProfileData?.name || mentorProfileData?.title || mentorProfileData?.firstName || 'Practitioner'}
                      showPresence={false}
                    />
                    <Box sx={{ flex: 1, textAlign: 'left' }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600, 
                          color: theme.palette.text.primary || '#0f172a',
                          fontSize: { xs: '15px', sm: '16px', md: '17px' },
                          letterSpacing: '-0.01em',
                          fontFamily: theme.typography.fontFamily,
                          mb: 0.25,
                        }}
                      >
                        {mentorProfileData?.name || mentorProfileData?.title || mentorProfileData?.firstName || 'Practitioner'}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.palette.text.secondary || '#64748b',
                          fontSize: { xs: '12px', sm: '13px' },
                          fontWeight: 400,
                        }}
                      >
                        Ready for consultations
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 0.75, sm: 1 },
                    pt: { xs: 1, sm: 1.25 },
                    borderTop: `1px solid ${theme.palette.divider || 'rgba(0, 0, 0, 0.1)'}`,
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary || '#475569', 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.5,
                        fontWeight: 400,
                      }}
                    >
                      <strong>Method:</strong> {
                        roomData?.checkInMethodOptions?.find(
                          (item) => item?.id === selectedCheckinMethod,
                        )?.title
                      }
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary || '#475569', 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.5,
                        fontWeight: 400,
                      }}
                    >
                      <strong>Room:</strong> {roomData?.title}
                    </Typography>
                    {clinicLocationData?.title && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: theme.palette.text.secondary || '#475569', 
                          fontSize: { xs: '13px', sm: '14px' },
                          lineHeight: 1.5,
                          fontWeight: 400,
                        }}
                      >
                        <strong>Clinic:</strong> {clinicLocationData.title}
                      </Typography>
                    )}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: theme.palette.text.secondary || '#475569', 
                        fontSize: { xs: '13px', sm: '14px' },
                        lineHeight: 1.5,
                        fontWeight: 400,
                      }}
                    >
                      <strong>Session:</strong> {
                        roomData?.checkInEventOptions?.find(
                          (item) => item?.id === selectedCheckinEventOption,
                        )?.title
                      }
                    </Typography>
                  </Box>
                </InfoCard>

                {supportTeamMemberCount > 0 && (
                  <Box
                    sx={{
                      mb: { xs: 2, sm: 2.5, md: 3 },
                      p: { xs: 2, sm: 2.5, md: 3 },
                      borderRadius: { xs: '20px', sm: '24px' },
                      background:
                        'linear-gradient(180deg, rgba(14, 165, 233, 0.08) 0%, rgba(248, 250, 252, 0.96) 100%)',
                      border: '1px solid rgba(14, 165, 233, 0.18)',
                      boxShadow: '0 14px 32px rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: { xs: 1.75, sm: 2 },
                      }}
                    >
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.text.primary || '#0f172a',
                            fontSize: { xs: '17px', sm: '18px', md: '20px' },
                            letterSpacing: '-0.01em',
                          }}
                        >
                          Team already in service
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.5,
                            color: theme.palette.text.secondary || '#64748b',
                            fontSize: { xs: '13px', sm: '14px' },
                            lineHeight: 1.6,
                          }}
                        >
                          Screening and therapy support currently checked in at {supportTeamLocationTitle}.
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '999px',
                          backgroundColor: 'rgba(14, 165, 233, 0.12)',
                          color: '#0369a1',
                          fontSize: '12px',
                          fontWeight: 700,
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {supportTeamMemberCount} in service
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
                        gap: { xs: 1.5, sm: 2 },
                      }}
                    >
                      {[
                        {
                          title: 'Screeners',
                          accent: '#0ea5e9',
                          members: checkedInScreeners,
                        },
                        {
                          title: 'Therapists',
                          accent: '#10b981',
                          members: checkedInTherapists,
                        },
                      ].map((section) => (
                        <Box
                          key={section.title}
                          sx={{
                            borderRadius: '18px',
                            backgroundColor: 'rgba(255, 255, 255, 0.88)',
                            border: `1px solid ${section.accent}22`,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              px: { xs: 1.5, sm: 2 },
                              py: 1.25,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                              borderBottom: `1px solid ${section.accent}1f`,
                              background: `linear-gradient(90deg, ${section.accent}14 0%, rgba(255, 255, 255, 0.92) 100%)`,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: theme.palette.text.primary || '#0f172a',
                                fontSize: { xs: '13px', sm: '14px' },
                              }}
                            >
                              {section.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: section.accent,
                                fontWeight: 700,
                                fontSize: '12px',
                              }}
                            >
                              {section.members.length} active
                            </Typography>
                          </Box>

                          <Box sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                            {section.members.length === 0 ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary || '#64748b',
                                  fontSize: { xs: '13px', sm: '14px' },
                                  lineHeight: 1.6,
                                  textAlign: 'left',
                                }}
                              >
                                {getSupportTeamEmptyState(section.title)}
                              </Typography>
                            ) : (
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr',
                                  gap: 1,
                                }}
                              >
                                {section.members.map((member, index) => (
                                  <Box
                                    key={`${section.title}-${member?.teacherId || index}`}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      p: { xs: 1.1, sm: 1.25 },
                                      borderRadius: '16px',
                                      border: `1px solid ${section.accent}1f`,
                                      backgroundColor: `${section.accent}08`,
                                      textAlign: 'left',
                                    }}
                                  >
                                    <AvatarComponentChat
                                      imageInHeader={getSupportTeamImage(member)}
                                      nameInHeader={member?.teacherName || section.title}
                                      showPresence={false}
                                      size={isMobile ? 'small' : undefined}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 700,
                                          color: theme.palette.text.primary || '#0f172a',
                                          fontSize: { xs: '13px', sm: '14px' },
                                          lineHeight: 1.35,
                                        }}
                                      >
                                        {member?.teacherName || 'Practitioner'}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          mt: 0.35,
                                          color: theme.palette.text.secondary || '#475569',
                                          fontSize: { xs: '12px', sm: '13px' },
                                          lineHeight: 1.5,
                                        }}
                                      >
                                        {member?.serviceLineTitle || section.title.slice(0, -1)}
                                        {member?.chamberRoomName ? ` in ${member.chamberRoomName}` : ''}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Compact Best Wishes */}
                <SuccessMessage>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#059669',
                      mb: { xs: 1, sm: 1.25 },
                      fontSize: { xs: '18px', sm: '20px', md: '22px' },
                      letterSpacing: '-0.01em',
                      fontFamily: theme.typography.fontFamily,
                    }}
                  >
                    All the best for your session!
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.text.secondary || '#64748b', 
                      fontSize: { xs: '13px', sm: '14px' },
                      fontWeight: 400,
                      lineHeight: 1.6,
                      maxWidth: { xs: '100%', sm: '450px' },
                      mx: 'auto',
                    }}
                  >
                    {supportTeamMemberCount > 0
                      ? `Your session is now active, and your support team is already helping patients at ${supportTeamLocationTitle}.`
                      : 'Your session is now active. We wish you a productive and successful consultation session today!'}
                  </Typography>
                </SuccessMessage>
              </Box>
            )}
          </Box>
        )}
        {/* Timeout Progress Bar - Receding line at bottom - Always at the end */}
        {page !== 'checkInFinalPage' && !roomOccupied && !checkInError && (
          <TimeoutProgressBar progress={(timeRemaining / TIMEOUT_DURATION) * 100} />
        )}
      </DialogContentBox>
    </>
  )

  // Function to restore scroll
  const restoreScroll = useCallback(() => {
    if (scrollRestoreRef.current) {
      const { originalBodyOverflow, originalHtmlOverflow, originalPosition, originalWidth, originalTop, scrollY } = scrollRestoreRef.current
      
      // Restore styles
      document.body.style.overflow = originalBodyOverflow || ''
      document.documentElement.style.overflow = originalHtmlOverflow || ''
      document.body.style.position = originalPosition || ''
      document.body.style.width = originalWidth || ''
      document.body.style.top = originalTop || ''
      
      // Restore scroll position
      window.scrollTo(0, scrollY)
      
      // Clear the ref
      scrollRestoreRef.current = null
    }
  }, [])

  // Internal state to control drawer open/close for smooth animation
  const [drawerOpen, setDrawerOpen] = useState(true)
  
  // Wrapped close function that ensures scroll is restored
  const handleClose = useCallback((event, reason) => {
    // Close drawer immediately for smooth animation
    setDrawerOpen(false)
    // Restore scroll and call parent close function after short delay
    restoreScroll()
    setTimeout(() => {
      closeFunction()
    }, 200) // Match exit transition duration
  }, [closeFunction, restoreScroll])

  // Update ref whenever handleClose changes
  useEffect(() => {
    handleCloseRef.current = handleClose
  }, [handleClose])

  // Prevent body scroll when dialog is open - Enhanced
  useEffect(() => {
    // Lock body scroll when component mounts
    const originalBodyOverflow = document.body.style.overflow
    const originalHtmlOverflow = document.documentElement.style.overflow
    const originalPosition = document.body.style.position
    const originalWidth = document.body.style.width
    const originalTop = document.body.style.top
    
    // Get current scroll position
    const scrollY = window.scrollY
    
    // Store for restoration
    scrollRestoreRef.current = {
      originalBodyOverflow,
      originalHtmlOverflow,
      originalPosition,
      originalWidth,
      originalTop,
      scrollY,
    }
    
    // Apply styles to prevent scrolling
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.width = '100%'
    document.body.style.top = `-${scrollY}px`
    
    return () => {
      // Restore body scroll when component unmounts
      // Use setTimeout to ensure this runs after MUI's cleanup
      setTimeout(() => {
        restoreScroll()
      }, 0)
    }
  }, [restoreScroll])

  // Mobile: Drawer from bottom with full modal behavior
  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={(event, reason) => {
          // Only allow closing via explicit user action
          if (reason && reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            handleClose(event, reason)
          }
        }}
        disableEscapeKeyDown={true}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            maxHeight: '95vh',
            height: 'auto',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: 'linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%)',
            // Removed backdropFilter to prevent blur issues on mobile
            boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.15), 0 -4px 16px rgba(0, 0, 0, 0.1)',
            width: '100%',
            maxWidth: '100%',
            zIndex: 1401, // Must be above backdrop (1399) to appear on top
            // Remove position relative to allow proper bottom slide animation
          },
        }}
        transitionDuration={{ enter: 300, exit: 200 }} // Faster exit for quick close
        ModalProps={{
          style: {
            zIndex: 1400, // Increased to ensure it's above all other elements
          },
          disableScrollLock: true, // We handle scroll lock manually
          disableEnforceFocus: false,
          disableAutoFocus: false,
        }}
        sx={{
          zIndex: 1400, // Increased to ensure it's above all other elements
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.4) !important',
            backdropFilter: 'none !important',
            WebkitBackdropFilter: 'none !important',
            filter: 'none !important',
            WebkitFilter: 'none !important',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1399,
            transition: 'none !important',
            animation: 'none !important',
            // Remove any pseudo-elements that might have blur
            '&::before': {
              backdropFilter: 'none !important',
              WebkitBackdropFilter: 'none !important',
              filter: 'none !important',
              WebkitFilter: 'none !important',
            },
            '&::after': {
              backdropFilter: 'none !important',
              WebkitBackdropFilter: 'none !important',
              filter: 'none !important',
              WebkitFilter: 'none !important',
            },
          },
        }}
      >
        {/* Drag handle indicator */}
        <Box
          sx={{
            width: { xs: 5, sm: 6 }, // 40px on mobile, 48px on larger
            height: { xs: 0.5, sm: 0.625 }, // 4px on mobile, 5px on larger
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: { xs: 0.25, sm: 0.375 }, // 2px on mobile, 3px on larger
            margin: { xs: '10px auto 6px', sm: '12px auto 8px' },
            cursor: 'grab',
            flexShrink: 0,
            '&:active': {
              cursor: 'grabbing',
            },
          }}
        />
        {renderContent()}
      </Drawer>
    )
  }

  // Desktop: Dialog with full modal behavior
  return (
    <StyledDialog
      open={true}
      onClose={(event, reason) => {
        // Only allow closing via explicit user action (Cancel button or completion)
        // Prevent backdrop click and escape key from closing
        if (reason && reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          handleClose(event, reason)
        }
      }}
      scroll="paper"
      maxWidth={false}
      fullWidth={false}
      disableEscapeKeyDown={true}
      PaperProps={{
        sx: {
          margin: { xs: 1, sm: 2, md: 3 },
          maxHeight: { xs: '95vh', sm: '92vh', md: '92vh' },
          width: '100%',
          maxWidth: { xs: 'calc(100% - 16px)', sm: '500px', md: '900px' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5) !important',
          backdropFilter: 'none !important',
          WebkitBackdropFilter: 'none !important',
          filter: 'none !important',
          WebkitFilter: 'none !important',
          transition: 'none !important',
          animation: 'none !important',
          zIndex: 1299,
        },
      }}
      sx={{
        zIndex: 1400, // Increased to ensure it's above all other elements
        '& .MuiDialog-container': {
          alignItems: { xs: 'flex-end', sm: 'center', md: 'center' },
          display: 'flex',
          justifyContent: 'center',
          zIndex: 1400,
        },
        '& .MuiDialog-paper': {
          position: 'relative',
          zIndex: 1400,
        },
        '& .MuiBackdrop-root': {
          zIndex: 1399,
        },
      }}
    >
      {renderContent()}
    </StyledDialog>
  )
}
